'use client';

import { Button, Text } from '@/design-system';
import type { IssuedCoupon } from '../../api/user/dto/response/app-onboarding-benefit.res.dto';
import { formattedDate } from '../../utils/date/formattedDate';
import BottomDrawerV2 from '../BottomDrawerV2';
import UImage from '../UImage';
import { COUPON_AMOUNT_TEXT } from './useMarketingConsentModal';

const COUPON_IMAGE_URL = `${process.env.NEXT_PUBLIC_S3_IMAGE_URL}/assets/app-install-promotion/marketing_policy_coupon.png`;

interface MarketingConsentCouponIssuedModalProps {
  /** 발급된 쿠폰 정보 (만료 안내에 사용) */
  coupon: IssuedCoupon | null;

  /** "쿠폰 쓰러 가기" 클릭 — 마켓으로 이동한다 */
  onClickGoToMarket: () => void;

  /** 발급 완료 바텀시트 닫기 */
  onClose: () => void;
}

/**
 * 마케팅 수신 동의 후 앱 전용 쿠폰 발급 완료 안내 바텀시트
 *
 * "동의하고 쿠폰 받기" 직후(OS 알림 안내와 별개로) 노출된다. 발급된 2,000원 쿠폰을 안내하고
 * "쿠폰 쓰러 가기" CTA로 마켓으로 유도한다.
 */
const MarketingConsentCouponIssuedModal = ({
  coupon,
  onClickGoToMarket,
  onClose,
}: MarketingConsentCouponIssuedModalProps) => {
  const expirationGuide = coupon
    ? `*${formattedDate(new Date(coupon.expiredAt), 'yyyy.MM.dd')}까지 사용 가능`
    : '*유효기간 7일';

  return (
    <BottomDrawerV2.Wrapper
      open
      onClose={onClose}
      borderRadius={16}
      fitContent
      backdropBlur={false}
    >
      <BottomDrawerV2.Header handleClose={onClose} />
      <div className="flex flex-col items-center gap-[20px] px-[16px] pb-[24px]">
        <div className="flex flex-col items-center gap-[8px] text-center">
          <Text variant="h3-bold" as="p" className="text-gray-900">
            쿠폰 발급 완료!
          </Text>
          <Text variant="h6-medium-140" as="p" className="text-gray-600">
            마켓에서 바로 사용할 수 있어요.
          </Text>
        </div>
        <div className="flex flex-col items-center gap-[8px]">
          <UImage
            src={COUPON_IMAGE_URL}
            alt={`APP 전용 ${COUPON_AMOUNT_TEXT} 쿠폰`}
            width={238}
            height={140}
          />
          <Text variant="h7-regular-100" as="p" className="text-gray-600">
            {expirationGuide}
          </Text>
        </div>
      </div>
      <div className="px-[16px] pb-[16px]">
        <Button
          width="full"
          className="bg-soil-brown-700"
          onClick={onClickGoToMarket}
        >
          쿠폰 쓰러 가기
        </Button>
      </div>
    </BottomDrawerV2.Wrapper>
  );
};

export default MarketingConsentCouponIssuedModal;
